'use client';

import { atom, getDefaultStore, useAtom, useStore } from 'jotai';
import { useEffect } from 'react';
import { toast } from '@workspace/ui/components/sonner';

// Section-based billing atoms
export const aiSectionsAtom = atom<number>(0);
export const aiSectionsLimitAtom = atom<number>(100);
export const aiTotalSectionsAtom = atom<number>(0);
export const aiSectionsLoadingAtom = atom<boolean>(true);

// Legacy atoms kept for backward compatibility (deprecated - will be removed)
/** @deprecated Use aiSectionsAtom instead */
export const aiCreditsAtom = atom<number>(0);
/** @deprecated No longer used */
export const aiThresholdUsdAtom = atom<number>(0);
/** @deprecated Use aiSectionsLoadingAtom instead */
export const aiCreditsLoadingAtom = aiSectionsLoadingAtom;

const baseUrl =
  typeof process === 'undefined'
    ? // @ts-ignore : import.meta.env is only available in Vite builds
      import.meta.env.VITE_NEST_API_URL
    : process.env.NEXT_PUBLIC_NEST_API_URL;

let sectionsStore = getDefaultStore();

function buildSectionsUrl() {
  return baseUrl ? `${baseUrl}/api/credits` : `/api/credits`;
}

// Credits are org-scoped, and the acting org is per-request (X-Organization-Id), not the
// session's default org. Defaults to the dashboard's URL org segment; the builder overrides
// this with its in-memory acting-org holder since its URLs carry no org.
let orgIdSource = (): string | null =>
  typeof window === 'undefined'
    ? null
    : (window.location.pathname.match(/^\/(\d+)(?=\/|$)/)?.[1] ?? null);

export function setAiCreditsOrgSource(source: () => string | null) {
  orgIdSource = source;
}

export async function refreshAiCredits() {
  try {
    const orgId = orgIdSource();
    const response = await fetch(buildSectionsUrl(), {
      credentials: 'include',
      ...(orgId ? { headers: { 'X-Organization-Id': orgId } } : {}),
    });
    if (!response || !response.ok) {
      return { sections: 0 };
    }
    const data = await response.json().catch(() => ({}));
    const payload = (data && (data.data || data)) || {};

    // Section-based billing data
    const sections = Number(payload.monthly_sections ?? 0);
    const sectionsLimit = Number(payload.section_limit ?? 100);
    const totalSections = Number(payload.total_sections ?? 0);

    sectionsStore.set(aiSectionsAtom, sections);
    sectionsStore.set(aiSectionsLimitAtom, sectionsLimit);
    sectionsStore.set(aiTotalSectionsAtom, totalSections);

    // Set legacy atoms to 0 for backward compatibility
    sectionsStore.set(aiCreditsAtom, 0);
    sectionsStore.set(aiThresholdUsdAtom, 0);

    return { sections };
  } catch {
    sectionsStore.set(aiSectionsAtom, 0);
    sectionsStore.set(aiSectionsLimitAtom, 100);
    sectionsStore.set(aiTotalSectionsAtom, 0);
    sectionsStore.set(aiCreditsAtom, 0);
    sectionsStore.set(aiThresholdUsdAtom, 0);
    return { sections: 0 };
  }
}

export function useAiCredits() {
  const store = useStore();
  const [isLoading, setIsLoading] = useAtom(aiSectionsLoadingAtom);

  // Section-based billing state
  const [sections, setSections] = useAtom(aiSectionsAtom);
  const [sectionsLimit, setSectionsLimit] = useAtom(aiSectionsLimitAtom);
  const [totalSections, setTotalSections] = useAtom(aiTotalSectionsAtom);

  // Legacy state (deprecated)
  const [credits] = useAtom(aiCreditsAtom);
  const [thresholdUsd] = useAtom(aiThresholdUsdAtom);

  useEffect(() => {
    sectionsStore = store;
  }, [store]);

  /** @deprecated Use requireSections instead */

  function requireSections(min = 1) {
    if (sections < min) {
      toast.error(
        `Insufficient AI pages. You have ${sections}/${sectionsLimit} pages remaining.`,
      );
      throw new Error('Insufficient AI pages');
    }
  }

  async function fetchAiCredits() {
    try {
      setIsLoading(true);
      const result = await refreshAiCredits();
      setIsLoading(false);
      return result.sections;
    } catch {
      setSections(0);
      setSectionsLimit(100);
      setTotalSections(0);
      setIsLoading(false);
      return 0;
    }
  }

  return {
    // Section-based billing (primary)
    sections,
    sectionsLimit,
    totalSections,
    requireSections,
    setSections,
    isLoading,
    fetchAiCredits,

    // Legacy fields (deprecated - kept for backward compatibility)
    /** @deprecated Always returns 0. Use sections instead */
    credits,
    /** @deprecated No longer used */
    thresholdUsd,

    /** @deprecated Use setSections instead */
    setCredits: setSections,
  };
}

export async function creditAwareFetch(
  fetcher: (url: string, opts?: RequestInit) => Promise<Response>,
  url: string,
  opts: RequestInit = {},
) {
  const res = await fetcher(url, opts);
  if (res.status === 402) {
    // Sync to server state
    await refreshAiCredits();

    // Parse error response
    try {
      const errorData = await res.clone().json();
      if (errorData.error?.includes('sections')) {
        toast.error('Insufficient AI pages. Please upgrade your plan or wait for renewal.');
      } else {
        toast.error('Insufficient AI pages');
      }
    } catch {
      toast.error('Insufficient AI pages');
    }
    return res;
  }
  if (res.ok) {
    // Always refresh to get the exact post-call balance from the server
    await refreshAiCredits();
  }
  return res;
}
